import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { getApiErrorMessage } from '@/helpers/apiError';
import {
  formatRemainingTimeLabel,
  getRemainingDurationMs,
  RECENT_OPERATION_SAMPLE_SIZE,
  SHOW_REMAINING_TIME_MIN_ITEMS,
  type BulkTravellerResult,
} from '@/helpers/bulkTravellerOperation';
import { toast } from '@/hooks/useToast';

interface UseBulkTravellerOperationProps {
  onProcessingChange: (processing: boolean) => void;
  onComplete: (failedTravellerIds: string[]) => void;
}

interface RunBulkTravellerOperationOptions<T> {
  items: T[];
  getItem: (item: T) => { id: string; name: string };
  execute: (item: T) => Promise<void>;
  afterAll?: () => Promise<void>;
  canStart?: boolean;
  showCompletionToasts?: boolean;
}

export const useBulkTravellerOperation = ({
  onProcessingChange,
  onComplete,
}: UseBulkTravellerOperationProps) => {
  const { formatMessage } = useIntl();

  const [isProcessing, setIsProcessing] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const [currentName, setCurrentName] = useState('');
  const [results, setResults] = useState<BulkTravellerResult[] | null>(null);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  const cancelledRef = useRef(false);
  const processingRef = useRef(false);
  const recentDurationsRef = useRef<number[]>([]);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!isProcessing) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isProcessing]);

  const run = useCallback(
    async <T>({
      items,
      getItem,
      execute,
      afterAll,
      canStart = true,
      showCompletionToasts = true,
    }: RunBulkTravellerOperationOptions<T>) => {
      if (!canStart || processingRef.current) {
        return;
      }

      cancelledRef.current = false;
      processingRef.current = true;
      recentDurationsRef.current = [];
      setIsProcessing(true);
      onProcessingChange(true);
      setProcessedCount(0);
      setProgressTotal(items.length);
      setResults(null);
      setRemainingMs(null);

      const fallbackError = formatMessage({
        defaultMessage: 'Ett fel inträffade',
      });
      const nextResults: BulkTravellerResult[] = [];

      try {
        for (const [index, item] of items.entries()) {
          if (cancelledRef.current) {
            break;
          }

          const { id, name } = getItem(item);
          setCurrentName(name);

          const startedAt = performance.now();

          try {
            await execute(item);
            nextResults.push({ id, name, status: 'success' });
          } catch (error) {
            nextResults.push({
              id,
              name,
              status: 'failed',
              message: getApiErrorMessage(error, fallbackError),
            });
          }

          const durations = recentDurationsRef.current;
          durations.push(performance.now() - startedAt);
          if (durations.length > RECENT_OPERATION_SAMPLE_SIZE) {
            durations.shift();
          }

          setRemainingMs(
            getRemainingDurationMs(durations, items.length - index - 1),
          );
          setProcessedCount(index + 1);
        }

        await afterAll?.();
      } finally {
        processingRef.current = false;

        if (!cancelledRef.current) {
          setResults(nextResults);
          setIsProcessing(false);
          onProcessingChange(false);
        }
      }

      if (cancelledRef.current) {
        return;
      }

      const successCount = nextResults.filter(
        (result) => result.status === 'success',
      ).length;
      const failedCount = nextResults.length - successCount;

      if (showCompletionToasts && successCount > 0) {
        toast.success(
          {
            title: formatMessage(
              {
                defaultMessage:
                  '{successCount, plural, one {# resenär uppdaterad} other {# resenärer uppdaterade}}',
              },
              { successCount },
            ),
          },
          { autoClose: 3000 },
        );
      }

      if (showCompletionToasts && failedCount > 0) {
        toast.error(
          {
            title: formatMessage(
              {
                defaultMessage:
                  '{failedCount, plural, one {# resenär kunde inte uppdateras} other {# resenärer kunde inte uppdateras}}',
              },
              { failedCount },
            ),
          },
          { autoClose: 5000 },
        );
      }

      onComplete(
        nextResults
          .filter((result) => result.status === 'failed')
          .map((result) => result.id),
      );
    },
    [formatMessage, onComplete, onProcessingChange],
  );

  const remainingTimeLabel = useMemo(
    () => formatRemainingTimeLabel(remainingMs, formatMessage),
    [formatMessage, remainingMs],
  );
  const showRemainingTime =
    isProcessing &&
    progressTotal > SHOW_REMAINING_TIME_MIN_ITEMS &&
    remainingTimeLabel !== null;

  return {
    isProcessing,
    isComplete: results !== null,
    processedCount,
    progressTotal,
    currentName,
    results,
    remainingTimeLabel,
    showRemainingTime,
    run,
  };
};
